import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ClosingCta } from "@/components/ClosingCta";
import { useLanguage, localizedPath } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { thumbUrl, srcSet } from "@/lib/images";
import { COLLABORATIONS } from "@/lib/collaborations";
import { ART_LABEL, ART_ORDER } from "@/lib/categories";
import { formatNoteDate } from "@/lib/dates";

// Fallback, solange keine Serie „Auf Startseite zeigen" trägt (z. B. Vorschau)
const FEATURED_SERIES = [
  { num: "01", name: "L'Aliança d'Anglès", img: "works/8f55f519-bcd0-487b-be8b-a1676eccbd16.webp", slug: "alianca-angles" },
  { num: "02", name: "El Celler de Can Roca", img: "works/fbdaa61e-e7e5-494e-a9b4-2becb59497a9.webp", slug: "el-celler-de-can-roca" },
  { num: "03", name: "Casamar", img: "works/f0ab380f-865f-45e1-8835-077c5a0cc4c9.webp", slug: "casamar" },
];

// Basis-URL einer Kachel: Cover aus der Datenbank oder Fallback-Pfad
const tileBase = (tile) => (tile.cover ? tile.cover : `/api/files/wimjansen/${tile.img}`);
const tileSrc = (tile, w) => `${tileBase(tile)}?w=${w}`;

// Standard-Texte für „Zusammenarbeit" — im Admin (Tab Startseite) editierbar
export const COLLAB_DEFAULTS = [
  {
    title_de: "Einzelauftrag",
    text_de: "Ein Tag in deinem Restaurant oder Hotel. Gerichte, Räume, Menschen bei der Arbeit, ungestellt und bei natürlichem Licht.",
    title_en: "Single assignment",
    text_en: "One day at your restaurant or hotel. Dishes, spaces, people at work, unposed and in natural light.",
  },
  {
    title_de: "Monatliche Begleitung",
    text_de: "Ich komme einmal im Monat vorbei und halte fest, was sich verändert: neue Gerichte, die Saison, das Team. So hast du immer aktuelle Bilder für Website, Social Media und Presse.",
    title_en: "Monthly collaboration",
    text_en: "I come by once a month and capture what changes: new dishes, the season, the team. That way you always have fresh images for your website, social media and press.",
  },
  {
    title_de: "Editorial & Reise",
    text_de: "Reportagen für Magazine und Destinationen, von der ersten Idee bis zur fertigen Bildstrecke.",
    title_en: "Editorial & travel",
    text_en: "Stories for magazines and destinations, from the first idea to the finished series.",
  },
];

// „So arbeite ich": vier Schritte, Texte DE/EN über i18n
const STEPS = [
  { title: "Erstes Gespräch", text: "Wir sprechen kurz über dein Haus, den Anlass und wofür du die Bilder brauchst. Danach bekommst du ein klares Angebot." },
  { title: "Der Tag vor Ort", text: "Ich arbeite leise und mit wenig Ausrüstung. Niemand muss posieren, dein Team arbeitet einfach weiter." },
  { title: "Auswahl und Bearbeitung", text: "Ich wähle die stärksten Bilder aus und bearbeite jedes einzeln. Du bekommst sie in voller Auflösung, fertig für Web und Druck." },
  { title: "Nutzungsrechte", text: "Vorab halten wir schriftlich fest, wofür du die Bilder nutzen darfst: Website, Social Media, Presse oder mehr. So gibt es später keine Überraschungen." },
];

// Erste ein bis zwei Sätze einer Notiz für den Teaser
const teaserText = (text = "") => {
  const sentences = text.match(/[^.!?]+[.!?]+/g);
  return sentences ? sentences.slice(0, 2).join(" ").trim() : text.slice(0, 220);
};

export default function Home() {
  const { t, lang } = useLanguage();
  const [collab, setCollab] = useState(null);
  const [homeSeries, setHomeSeries] = useState(null);
  // Seed aus dem statischen HTML: die App zeigt beim ersten Rendern sofort
  // dieselben Kacheln, bis die API frische Daten liefert (kein Aufflackern)
  const [homeTiles, setHomeTiles] = useState(() =>
    typeof window !== "undefined" && Array.isArray(window.__HOME_TILES__) && window.__HOME_TILES__.length
      ? window.__HOME_TILES__
      : null
  );
  const [testimonials, setTestimonials] = useState([]);
  const [latestNote, setLatestNote] = useState(null);
  useSeo({
    title: "Wim Jansen Delgado · Fotograf aus Hamburg für Restaurants, Hotels und Reisen",
    description:
      "Wim Jansen Delgado ist Fotograf aus Hamburg, spezialisiert auf Restaurants, Hotels und Reisen. Gefunden, nicht inszeniert.",
    path: "/",
  });

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => setCollab(res.data.collab || null))
      .catch(() => setCollab(null));
    // Kacheln aus den veröffentlichten Serien mit „Auf Startseite zeigen"
    api
      .get("/projects")
      .then((res) => setHomeSeries(res.data || []))
      .catch(() => setHomeSeries([]));
    // Frei gewählte Startseiten-Bilder (Flag im Admin pro Bild); leer = Serien-Kacheln
    api
      .get("/home-tiles")
      .then((res) => setHomeTiles(res.data || []))
      .catch(() => setHomeTiles([]));
    // Kundenstimmen (Abschnitt bleibt leer ausgeblendet, solange keine eingetragen sind)
    api
      .get("/testimonials")
      .then((res) => setTestimonials(res.data || []))
      .catch(() => setTestimonials([]));
    // Neueste Fieldnote für den Teaser
    api
      .get("/fieldnotes")
      .then((res) => setLatestNote((res.data || [])[0] || null))
      .catch(() => setLatestNote(null));
  }, []);

  const marked = (homeSeries || [])
    .filter((p) => p.show_home && !p.protected)
    .sort((a, b) => (a.home_order || 99) - (b.home_order || 99))
    .slice(0, 3);
  // Frei gewählte Bilder (Flag im Admin) haben Vorrang vor den Serien-Kacheln
  const tiles = homeTiles?.length
    ? homeTiles
    : marked.length
      ? marked
      : (homeSeries === null ? [] : FEATURED_SERIES);
  // Feste Rubrik-Reihenfolge: Restaurants, Hotels, Reisen (ohne Rubrik hinten)
  const orderedTiles = [...tiles].sort((a, b) => (ART_ORDER[a.art] ?? 99) - (ART_ORDER[b.art] ?? 99));

  // Gespeicherte Texte gewinnen, leere Felder fallen auf den Standard zurück
  const blocks = COLLAB_DEFAULTS.map((d, i) => {
    const c = collab && collab[i];
    if (!c) return d;
    return {
      title_de: c.title_de || d.title_de,
      text_de: c.text_de || d.text_de,
      title_en: c.title_en || d.title_en,
      text_en: c.text_en || d.text_en,
    };
  });

  const noteText = latestNote
    ? (lang === "en" && latestNote.text_en ? latestNote.text_en : latestNote.text)
    : "";
  const noteLocation = latestNote
    ? (lang === "en" && latestNote.location_en ? latestNote.location_en : latestNote.location)
    : "";

  return (
    <div data-testid="home-page">
      {/* Hero: Leitsatz */}
      <section
        data-testid="hero-section"
        className="relative flex min-h-[62vh] flex-col justify-center overflow-hidden"
      >
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 lg:px-16">
          <h1 className="sr-only">{t("Wim Jansen Delgado, Fotograf aus Hamburg")}</h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-name-line"
          >
            <p className="font-mono text-sm uppercase tracking-[0.15em] text-smoke">
              {t("Wim Jansen Delgado")}
            </p>
            <p className="mt-3 font-mono text-[13px] uppercase tracking-[0.15em] text-smoke/80" data-testid="hero-specialty-line">
              {t("Fotograf aus Hamburg")}
            </p>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-quote"
            className="mt-14 max-w-6xl text-balance font-serif text-4xl font-light leading-tight tracking-tight text-bone md:mt-20 md:text-6xl"
          >
            {t("Die besten Momente lassen sich nicht")} <span className="italic text-ember">{t("planen.")}</span>
            <span className="mt-3 block text-smoke md:mt-4">{t("Ich bin da, wenn sie passieren.")}</span>
          </motion.p>
        </div>
      </section>

      {/* Drei Projektkacheln */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-12 lg:px-16" data-testid="series-strip">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {orderedTiles.map((s, i) => {
            const name = lang === "en" ? s.name_en || t(s.name) : s.name;
            const label = s.art && ART_LABEL[s.art] ? t(ART_LABEL[s.art]) : name;
            return (
            <Reveal key={s.slug} delay={i * 0.12}>
              <Link
                to={localizedPath(`/projekte/${s.slug}`, lang)}
                data-testid={`series-link-${s.slug}`}
                className="group block"
              >
                <img
                  src={tileSrc(s, 800)}
                  srcSet={`${tileSrc(s, 800)} 800w, ${tileSrc(s, 1400)} 1400w`}
                  sizes="(max-width: 767px) 100vw, 33vw"
                  alt={
                    (lang === "en" ? s.alt_en : s.alt_de) ||
                    (lang === "en" ? `${label}, photography by Wim Jansen Delgado` : `${label}, Fotografie von Wim Jansen Delgado`)
                  }
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  className="aspect-[5/7] w-full object-cover transition-opacity duration-500 group-hover:opacity-80"
                />
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-smoke">
                  {String(i + 1).padStart(2, "0")} · {label}
                </p>
              </Link>
            </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-wrap items-center justify-between gap-6">
            <Link
              to={localizedPath("/projekte", lang)}
              data-testid="home-all-projects-link"
              className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-smoke transition-colors duration-300 hover:text-ember-deep"
            >
              {t("Alle Projekte")}
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
            <Link
              to={localizedPath("/kontakt", lang)}
              data-testid="home-project-cta"
              className="inline-flex items-center gap-3 border border-ember-deep bg-ember-deep px-10 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-ember-deep"
            >
              {t("Projekt anfragen")} <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Auftraggeber & Partner direkt unter den Kacheln */}
      <section className="border-t border-black/10 bg-coal" data-testid="collaborations-section">
        <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 md:py-16 lg:px-16">
          <Reveal>
            <p className="mb-8 font-mono text-sm uppercase tracking-[0.15em] text-smoke">
              {t("Auftraggeber & Partner")}
            </p>
            <p className="max-w-5xl font-serif text-xl font-light leading-loose text-bone md:text-3xl md:leading-relaxed">
              {COLLABORATIONS.map((c, i) => (
                <span key={c}>
                  <span className="whitespace-nowrap">
                    {c}
                    {i < COLLABORATIONS.length - 1 && (
                      <span className="text-smoke/50" aria-hidden="true">{" ·"}</span>
                    )}
                  </span>
                  {i < COLLABORATIONS.length - 1 ? " " : ""}
                </span>
              ))}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Zusammenarbeit */}
      <section className="border-t border-black/10" data-testid="collab-section">
        <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 md:py-16 lg:px-16">
          <Reveal>
            <p className="mb-10 font-mono text-sm uppercase tracking-[0.15em] text-smoke">
              {t("Zusammenarbeit")}
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-x-10">
            {blocks.map((b, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`collab-block-${i + 1}`}>
                  <h2 className="font-serif text-2xl font-light tracking-tight text-bone">
                    {lang === "en" ? b.title_en : b.title_de}
                  </h2>
                  <p className="mt-4 text-[17px] font-light leading-[1.7] text-bone md:text-lg">
                    {lang === "en" ? b.text_en : b.text_de}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* So arbeite ich: vier Schritte */}
      <section className="border-t border-black/10" data-testid="so-arbeite-ich-section">
        <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 md:py-16 lg:px-16">
          <Reveal>
            <p className="mb-10 font-mono text-sm uppercase tracking-[0.15em] text-smoke">
              {t("So arbeite ich")}
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-10">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`work-step-${i + 1}`}>
                  <p className="font-mono text-[11px] tracking-[0.25em] text-smoke">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-3 font-serif text-xl font-light tracking-tight text-bone md:text-2xl">
                    {t(s.title)}
                  </h3>
                  <p className="mt-3 text-[17px] font-light leading-[1.7] text-bone md:text-lg">
                    {t(s.text)}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Kundenstimmen: nur sichtbar, wenn Zitate eingetragen sind */}
      {testimonials.length > 0 && (
        <section className="border-t border-black/10" data-testid="testimonials-section">
          <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 md:py-16 lg:px-16">
            <Reveal>
              <p className="mb-10 font-mono text-sm uppercase tracking-[0.15em] text-smoke">
                {t("Kundenstimmen")}
              </p>
            </Reveal>
            <div className={`grid grid-cols-1 gap-10 md:gap-x-12 ${
              testimonials.length > 1 ? "md:grid-cols-2" : ""
            } ${testimonials.length > 2 ? "lg:grid-cols-3" : ""}`}>
              {testimonials.map((q, i) => {
                const quote = lang === "en" && q.text_en ? q.text_en : q.text_de;
                const byline = [q.role, q.house].filter(Boolean).join(", ");
                return (
                  <Reveal key={q.id} delay={i * 0.1}>
                    <figure data-testid={`testimonial-${i + 1}`}>
                      <blockquote className="font-serif text-xl font-light italic leading-relaxed text-bone md:text-2xl">
                        {lang === "en" ? `“${quote}”` : `„${quote}“`}
                      </blockquote>
                      <figcaption className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-smoke">
                        {q.name}{byline && ` · ${byline}`}
                      </figcaption>
                    </figure>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Neueste Fieldnote als Teaser */}
      {latestNote && (
        <section className="border-t border-black/10" data-testid="latest-fieldnote-teaser">
          <div className="mx-auto max-w-7xl px-6 py-12 md:px-12 md:py-16 lg:px-16">
            <Reveal>
              <p className="mb-10 font-mono text-sm uppercase tracking-[0.15em] text-smoke">
                {t("Neueste Fieldnote")}
              </p>
            </Reveal>
            <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-14">
              {latestNote.images?.length > 0 && (
                <Reveal>
                  {/* Polaroid-Stil wie auf der Fieldnotes-Seite: weißer Rand,
                      natürliche Proportion, leichte Neigung */}
                  <div className="w-fit max-w-full -rotate-1 bg-white p-2 shadow-[0_2px_24px_rgba(28,26,23,0.10)] transition-transform duration-500 hover:rotate-0 md:p-2.5">
                    <img
                      src={thumbUrl(latestNote.images[0].image, 1000)}
                      srcSet={srcSet(latestNote.images[0].image, [700, 1000, 1400])}
                      sizes="(max-width: 767px) 100vw, 620px"
                      alt={
                        (lang === "en" ? latestNote.alt_en || latestNote.alt_de : latestNote.alt_de) ||
                        `${noteLocation ? `${noteLocation}, ` : ""}${lang === "en" ? "Fieldnote by" : "Fieldnote von"} Wim Jansen Delgado`
                      }
                      loading="lazy"
                      decoding="async"
                      data-testid="fieldnote-teaser-image"
                      className="block h-auto w-full md:h-96 md:w-auto md:max-w-full"
                    />
                  </div>
                </Reveal>
              )}
              <Reveal delay={0.1}>
                <div>
                  {(latestNote.date || noteLocation) && (
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-smoke">
                      {[latestNote.date ? formatNoteDate(latestNote.date, lang) : "", noteLocation].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <p className="mt-4 max-w-lg text-[17px] font-light leading-[1.7] text-bone md:text-lg" data-testid="fieldnote-teaser-text">
                    {teaserText(noteText)}
                  </p>
                  <Link
                    to={localizedPath("/fieldnotes", lang)}
                    data-testid="fieldnote-teaser-link"
                    className="mt-6 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-ember-deep transition-colors duration-300 hover:text-ember"
                  >
                    {t("Weiterlesen")} <ArrowRight size={13} />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      <ClosingCta />
    </div>
  );
}
